'use client';

import { useQuery } from '@tanstack/react-query';

import { getFamilyBookingsAction } from '~/(tenant)/bookings/_lib/server/family-bookings-action';

type FamilyBookingsResult = Awaited<ReturnType<typeof getFamilyBookingsAction>>;

type FamilyBookingAccount = Extract<
  FamilyBookingsResult,
  { success: true }
>['data'][number];

/**
 * One upcoming-bookings fetch shared by every native home section, so the
 * `get_family_bookings` RPC runs once per home load. `fetchedAt` is the
 * moment the response arrived; sections derive their "now" from it so
 * their output only changes when the data is refetched.
 */
export interface UpcomingFamilyBookings {
  fetchedAt: number;
  accounts: FamilyBookingAccount[];
}

export function flattenFamilyBookings<Entry>(
  accounts: FamilyBookingAccount[],
): Entry[] {
  return accounts.flatMap((account) =>
    Array.isArray(account.bookings) ? (account.bookings as Entry[]) : [],
  );
}

/** 6am today, or 6am yesterday before 6am — the native home's day boundary. */
export function upcomingCutoff(fetchedAt: number): Date {
  const cutoff = new Date(fetchedAt);
  if (cutoff.getHours() < 6) cutoff.setDate(cutoff.getDate() - 1);
  cutoff.setHours(6, 0, 0, 0);
  return cutoff;
}

/** Sessions starting before 6am belong to the previous booking day. */
export function isAfterCutoff(
  booking: { booking_date?: string | null; start_time?: string | null },
  cutoff: Date,
): boolean {
  const dt = new Date(`${booking.booking_date}T${booking.start_time}`);
  const [hour] = (booking.start_time || '').split(':').map(Number);
  if ((hour ?? 0) < 6) dt.setDate(dt.getDate() + 1);
  return dt > cutoff;
}

export function useNativeHomeUpcomingFamilyBookings<Selected>({
  userId,
  accountId,
  enabled,
  select,
}: {
  userId: string | undefined;
  accountId: string;
  enabled: boolean;
  select: (data: UpcomingFamilyBookings) => Selected;
}) {
  return useQuery({
    queryKey: ['native-home-upcoming-family-bookings', userId, accountId],
    queryFn: async (): Promise<UpcomingFamilyBookings> => {
      // Server action, not the browser client: class enrollments join session
      // rows the customer can't read under RLS.
      const result = await getFamilyBookingsAction({ includePast: false });

      return {
        fetchedAt: Date.now(),
        accounts: result.success ? result.data : [],
      };
    },
    select,
    enabled: enabled && !!userId,
    staleTime: 60_000,
  });
}
